import {useEffect, useState} from 'react';
import ICON_UP_ARROW_LIGHT from '../../../public/static/v4/commonSub/icon-up-arrow-light.svg';
import ICON_UP_ARROW from '../../../public/static/v4/commonSub/icon-up-arrow.svg';
import Text from '../../common/component/Text';
import useTheme from '../../common/hook/useTheme';

interface IOption<T> {
  key: T;
  value: string;
}
interface IProps<T> {
  wrapperClassName?: string;
  options: Array<IOption<T>>;
  placeholder: string;
  disabled?: boolean;
  className?: string;
  textClassName?: string;
  isReset?: boolean;
  onSelect?: (dataKey: T) => void;
}

export default function DropDownV4<T extends string>({
  wrapperClassName,
  options,
  placeholder,
  disabled = false,
  className,
  textClassName,
  isReset,
  onSelect,
}: IProps<T>) {
  const {isLightMode} = useTheme();

  const [selectedKey, setSelectedKey] = useState<string>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isReset) {
      setSelectedKey(undefined);
    }
  }, [isReset]);

  return (
    <div className={cn('relative cursor-pointer', wrapperClassName)}>
      <Text
        as="div"
        className={cn(className, DROP_DOWN_CN, textClassName, {
          '!bg-transparent !border-b-[1px] border-grey-02 !text-grey-03 light:border-grey-03':
            disabled,
          'rounded-t-8 !border-grey-03': isOpen,
          'rounded-8': !isOpen,
        })}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {options.find(({key}) => key === selectedKey)?.value || placeholder}
      </Text>
      <img
        className={cn('absolute right-18 top-1/2 -translate-y-1/2', {
          'rotate-180': !isOpen,
          'opacity-10': disabled,
        })}
        src={isLightMode ? ICON_UP_ARROW_LIGHT : ICON_UP_ARROW}
        alt={isOpen ? 'Close dropbox' : 'Open dropbox'}
        width={14}
        height={8}
      />
      {isOpen && (
        <>
          <div className="absolute w-full h-1 z-2 bg-grey-03 light:bg-grey-02" />
          <div className={cn(DROP_DOWN_BOX_CN)}>
            {options.map(({key, value}) => {
              return (
                <Text
                  key={key}
                  className={textClassName}
                  onClickCapture={() => {
                    setSelectedKey(key);
                    setIsOpen(false);
                    onSelect?.(key);
                  }}
                >
                  {value}
                </Text>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const DROP_DOWN_CN = cn(
  'py-18 pl-16 bg-grey-01 cursor-pointer leading-[1.43]',
  'light:bg-white',
  'border border-solid border-b-0 border-transparent'
);

const DROP_DOWN_BOX_CN = cn(
  'absolute w-full py-16 px-18 bg-grey-01 leading-[4] rounded-b-8 z-1',
  'border border-t-0 border-solid border-grey-03',
  'light:bg-white'
);
